import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import {
  Spin,
  Typography,
  Button,
  Table,
  message,
  Modal,
  Drawer,
  Input,
} from 'antd';
import { Link } from 'react-router-dom';
import { RightOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import requireAuth from '../../../hocs/requireAuth';
import ContentPageHeader from '../../../components/contentPageHeader';
import { CREATE_OFFERINGS } from '../../../constants/routes';
import {
  offeringsTableColumn,
  offeringTableFilterList,
} from '../../../constants/offerings';
import { offeringManagement } from '../../../redux/actions/offeringAction';
import {
  generatePaginationObj,
  hasPermission,
} from '../../../utilities/helpers';
import {
  OFFERING_DELETE_SUCCESS,
  OFFERING_STATUS_UPDATE_SUCCESS,
} from '../../../constants/messages';
import OfferingDetails from '../../../components/offerings/offeringDetails';
import {
  ROLE_MARKETING_ADMIN,
  ROLE_MARKETING_MANAGER,
} from '../../../constants/roles';
import { salesChannelManagement } from '../../../redux/actions/marketPlaceAction';
import TableFilters from '../../../components/tableFilters';
import ShowPaginationLimits from '../../../components/showPaginationLimits';

const { Text } = Typography;
const { confirm } = Modal;

const OfferingsScreen = () => {
  const [loading, setLoading] = useState(false);
  const selectedCompany = useSelector((state) =>
    get(state, 'auth.selectedCompany')
  );
  const userComapnies = useSelector((state) =>
    get(state, 'auth.userCompanies')
  );
  const role = useSelector((state) => get(state, 'auth.role'));
  const isMarketingUser = hasPermission(
    [ROLE_MARKETING_ADMIN, ROLE_MARKETING_MANAGER],
    [get(role, 'role_code')]
  );

  const [viewOfferingDrawer, setViewOfferingDrawer] = useState({
    visible: false,
    data: null,
  });

  const toggleViewOfferingDrawer = (data = null) => {
    setViewOfferingDrawer({
      visible: !get(viewOfferingDrawer, 'visible'),
      data,
    });
  };

  const { permissions } = useSelector((state) => get(state, 'auth.role'));
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState({
    data: [],
    filters: null,
    sorters: null,
    pagination: {
      offset: 0,
      limit: 10,
      total: 0,
      current: 1,
    },
    filterChips: null,
  });

  const [salesChannelList, setSalesChannelList] = useState([]);
  const [tempFilter, setTempFilter] = useState({
    filterDropdown: {
      marketplace: false,
      brand: false,
      availability: false,
      showStatus: false,
    },
    tempFilterChips: {
      sales_channel_id: undefined,
      company_id: undefined,
      availability: undefined,
      is_active: undefined,
    },
    filterValues: {
      sales_channel_id: undefined,
      company_id: undefined,
      availability: undefined,
      is_active: undefined,
    },
  });

  const { offset, limit } = generatePaginationObj(
    get(tableData, 'pagination.current'),
    get(tableData, 'pagination.limit')
  );

  const fetchSalesChannelList = () => {
    setLoading(true);
    dispatch(salesChannelManagement('compact.list.sales_channels'))
      .then((result) => {
        setLoading(false);
        setSalesChannelList(get(result, 'data'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchOfferings = (
    page = 1,
    filterObj = {},
    sorterObj = {},
    filterChipsObj = {},
    perPagelimit = get(tableData, 'pagination.limit')
  ) => {
    setLoading(true);
    const pageObj = generatePaginationObj(page, perPagelimit);
    dispatch(
      offeringManagement('list.offerings', {
        ...pageObj,
        company_id: isMarketingUser ? undefined : get(selectedCompany, 'id'),
        ...sorterObj,
        filters: filterObj,
      })
    )
      .then((result) => {
        setLoading(false);
        setTableData({
          data: get(result, 'data'),
          filters: filterObj,
          sorters: sorterObj,
          pagination: {
            ...pageObj,
            total: get(result, 'total'),
            current: page,
          },
          filterChips: filterChipsObj,
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDeleteOffering = (id, companyId) => {
    setLoading(true);
    dispatch(
      offeringManagement('delete.offering', {
        company_id: companyId,
        id,
      })
    )
      .then(() => {
        setLoading(false);
        message.success(OFFERING_DELETE_SUCCESS);
        fetchOfferings(get(tableData, 'pagination.current'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onConfirmDelete = (id, companyId) => {
    confirm({
      title: 'Are you sure!',
      content: 'Do you want to delete this Offering?',
      onOk() {
        onDeleteOffering(id, companyId);
      },
      onCancel() {},
    });
  };

  const updateOfferingStatus = (companyId, id, isChecked) => {
    setLoading(true);
    const payload = {
      company_id: companyId,
      id,
      is_active: isChecked,
    };
    dispatch(offeringManagement('update.offering', payload))
      .then(() => {
        setLoading(false);
        message.success(OFFERING_STATUS_UPDATE_SUCCESS);
        fetchOfferings(
          get(tableData, 'pagination.current'),
          get(tableData, 'filters'),
          get(tableData, 'sorters'),
          get(tableData, 'filterChips'),
          get(tableData, 'pagination.limit')
        );
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchOfferings();
    fetchSalesChannelList();
  }, [selectedCompany]);

  const onOfferingSearch = debounce((initialValue) => {
    fetchOfferings(
      get(tableData, 'pagination.current'),
      {
        ...get(tableData, 'filters'),
        name: initialValue,
      },
      get(tableData, 'sorters'),
      get(tableData, 'filterChips')
    );
  }, 500);

  return (
    <Spin spinning={loading}>
      <div>
        <div>
          <ContentPageHeader
            border
            title={[
              <Text className="content-page-header-heading">Product</Text>,
              <RightOutlined className="content-page-header-heading" />,
              <Text className="content-page-header-heading">Offerings</Text>,
            ]}
            extra={[
              hasPermission(permissions, ['PERM_ADD_PRODUCT_OFFERING']) && (
                <Link to={CREATE_OFFERINGS} key="1">
                  <Button
                    className="default-primary-btn"
                    type="primary"
                    icon={<PlusOutlined />}
                  >
                    Add New Offerings
                  </Button>
                </Link>
              ),
            ]}
          />
        </div>

        <div className="mt-30" style={{ width: '200px' }}>
          <Input
            placeholder="Search by Offering Name"
            onChange={(e) => {
              onOfferingSearch(e.target.value);
            }}
          />
        </div>

        <div
          className="d-flex filter mt-10"
          style={{ alignItems: 'flex-start' }}
        >
          <TableFilters
            filtersList={offeringTableFilterList(
              salesChannelList,
              userComapnies
            )}
            tableData={tableData}
            fetchTableData={fetchOfferings}
            tempFilter={tempFilter}
            setTempFilter={setTempFilter}
            roleCode={get(role, 'role_code')}
          />
        </div>

        <div
          className="d-flex filter"
          style={{ justifyContent: 'space-between' }}
        >
          <ShowPaginationLimits
            tableData={tableData}
            offset={offset}
            limit={limit}
            fetchTableData={fetchOfferings}
            filterChips={get(tempFilter, 'tempFilterChips')}
          />
        </div>

        <div className="product-table-container">
          <Table
            size="small"
            rowKey="id"
            className="product-table"
            dataSource={get(tableData, 'data')}
            columns={offeringsTableColumn({
              roleCode: get(role, 'role_code'),
              onConfirmDelete,
              onViewDetails: toggleViewOfferingDrawer,
              updateOfferingStatus,
              hasEditPermission: hasPermission(permissions, [
                'PERM_UPDATE_PRODUCT_OFFERING',
              ]),
              hasDeletePermission: hasPermission(permissions, [
                'PERM_DELETE_PRODUCT_OFFERING',
              ]),
            })}
            onChange={(pagimationObj, filterObj, sorterObj) => {
              fetchOfferings(
                get(pagimationObj, 'current'),
                get(tableData, 'filters'),
                get(sorterObj, 'order')
                  ? {
                      sortBy: get(sorterObj, 'field'),
                      sortDir:
                        get(sorterObj, 'order') === 'ascend' ? 'ASC' : 'DESC',
                    }
                  : {},
                get(tempFilter, 'tempFilterChips')
              );
            }}
            pagination={{
              showSizeChanger: false,
              position: ['bottomCenter'],
              hideOnSinglePage: true,
              pageSize: get(tableData, 'pagination.limit'),
              total: get(tableData, 'pagination.total'),
              current: get(tableData, 'pagination.current'),
            }}
          />
        </div>
      </div>
      <Drawer
        title="Offering Overview"
        destroyOnClose
        placement="right"
        width="65%"
        onClose={toggleViewOfferingDrawer}
        open={get(viewOfferingDrawer, 'visible')}
      >
        <OfferingDetails offering={get(viewOfferingDrawer, 'data')} />
      </Drawer>
    </Spin>
  );
};

export default requireAuth(OfferingsScreen);
